<template>
  <assistant-step>
    <gc-form class="assistant-form">
      <gc-row>
        <gc-col cols="12">
          <h2 class="gc-text-primary gc-text-center gc-text-3xl">
            {{ $t('emobilityInterest.title') }}
          </h2>
        </gc-col>
      </gc-row>
      <gc-row class="gc-flex gc-justify-center">
        <gc-col cols="12" class="gc-flex gc-flex-col gc-items-center">
          <gc-form
            v-model="formValid"
            @submit.prevent="onNext()"
            autocomplete="on"
          >
            <form-field
              class="interest-choice"
              :form-data="formData"
              field-name="interest"
              :hide-label="true"
              :step="step"
              :assistantContext="assistantContext"
              @component:input="onNext"
            >
              <template #customOption="{ option }">
                <div
                  class="gc-flex gc-flex-col gc-justify-start gc-items-center"
                >
                  <div v-if="option.image" class="gc-mb-2">
                    <gc-image
                      :src="`${baseUrl}/${option.image.file}`"
                      :height="option.image.options.height"
                      :width="option.image.options.width"
                    />
                  </div>
                  <div
                    class="gc-multiple-choice-option__content gc-text-center"
                  >
                    <span
                      class="text-subtitle-1 gc-font-bold"
                      v-html="$t(optionLabelString(option.value))"
                    ></span>
                    <small
                      v-if="$te(optionDescriptionString(option.value))"
                      class="gc-block gc-text-opacity-75"
                    >
                      {{ $t(optionDescriptionString(option.value)) }}
                    </small>
                  </div>
                </div>
              </template>
            </form-field>
          </gc-form>
        </gc-col>
      </gc-row>
      <gc-row>
        <gc-col cols="12">
          <div class="gc-text-sm gc-text-center gc-text-gray-400">
            <span class="gc-text-primary">* </span>
            <span v-html="$t('emobilityInterest.installCheckHint')"></span>
          </div>
        </gc-col>
      </gc-row>
    </gc-form>
  </assistant-step>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from '@vue/composition-api';
import AssistantStep from '@/components/AssistantStep.vue';
import GcForm from '@/components/primitives/GcForm.vue';
import GcImage from '@/components/primitives/GcImage.vue';
import AssistantStepNavigation from '@/components/AssistantStepNavigation.vue';
import { getStepContext } from '@/lib/context';
import { stepComponentProps } from '@/lib/steps/helper';
import FormField from '@/components/FormField.vue';
import GcCol from '@/components/primitives/GcCol.vue';
import GcRow from '@/components/primitives/GcRow.vue';
import { StepId } from '~/consts/assistant-steps';
import { sendEvent } from '@/lib/analytics/dataLayer';
import {
  getOptionDescriptionString,
  getOptionLabelString,
} from '@/lib/forms/helper';
import i18n from '@/plugins/i18n';

export default defineComponent({
  components: {
    AssistantStep,
    AssistantStepNavigation,
    FormField,
    GcCol,
    GcForm,
    GcRow,
    GcImage,
  },
  props: {
    ...stepComponentProps,
  },
  setup: (props, { emit }) => {
    const formData = getStepContext(props.assistantContext, props.step);
    const formValid = ref(false);
    const baseUrl = process.env.VUE_APP_BASE_URL;

    // Prefer the htmlLabel translation (rendered via v-html) over the plain
    // label, which is also used for the summary and mail output.
    const optionLabelString = (optionValue: string) => {
      const htmlLabelString = `${StepId.EMOBILITY_INTEREST}.formFields.interest.options.${optionValue}.htmlLabel`;
      return i18n.te(htmlLabelString)
        ? htmlLabelString
        : getOptionLabelString(
            StepId.EMOBILITY_INTEREST,
            'interest',
            optionValue,
          );
    };

    const optionDescriptionString = (optionValue: string) =>
      getOptionDescriptionString(
        StepId.EMOBILITY_INTEREST,
        'interest',
        optionValue,
      );

    const onNext = () => {
      const interestValue = formData.value.interest;
      const target = interestValue.includes('buy')
        ? StepId.EMOBILITY_BUY
        : StepId.ASSISTANT_SUBMIT;

      emit('transition', {
        event: 'JUMP_TO_' + target.toUpperCase(),
      });
    };

    return {
      baseUrl,
      formData,
      formValid,
      onNext,
      optionDescriptionString,
      optionLabelString,
    };
  },
});
</script>
